import React from 'react';
import { Lock } from 'lucide-react';
import { UnlockButton } from './UnlockButton';
import { PLAN_PRICES_USD, formatUsd } from '../../../data/pricingPlans';
import { formatSignalCount, isLockedValue, type NormalizedPattern, type ReportFinding } from '../../../utils/scanReport';
import type { CheckoutState } from '../../../hooks/useReportUnlock';
interface LockedEvidencePreviewProps {
  patterns: NormalizedPattern[];
  /** Teaser findings — `example === "LOCKED"` renders as a lock, never as text. */
  findings: ReportFinding[];
  state: CheckoutState;
  onUnlock: () => void;
}
interface EvidenceRow {
  name: string;
  locked: boolean;
  meta: string;
}

// Fixed redaction widths — purely visual, never derived from real messages.
const REDACTION_WIDTHS = ['92%', '74%', '86%', '64%', '80%'];
function buildRows(patterns: NormalizedPattern[], findings: ReportFinding[]): EvidenceRow[] {
  if (findings.length > 0) {
    // Only the pattern name and lock state are read — evidence text is never rendered here.
    return findings.map(finding => ({
      name: finding.pattern,
      locked: isLockedValue(finding.example),
      meta: 'Evidence detected'
    }));
  }
  return patterns.map(pattern => ({
    name: pattern.type,
    locked: pattern.evidenceLocked,
    meta: formatSignalCount(pattern.signalCount)
  }));
}
export function LockedEvidencePreview({
  patterns,
  findings,
  state,
  onUnlock
}: LockedEvidencePreviewProps) {
  const rows = buildRows(patterns, findings);
  if (rows.length === 0) return null;
  return <section aria-labelledby="evidence-heading" data-id="element-232" data-mp="3dmcr2quo357i">
      <div className="mb-5 flex items-end justify-between gap-4" data-id="element-233" data-mp="3skeun11p7qor">
        <div data-id="element-234" data-mp="3shiyni9evc2y">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-forensic-subtle" data-id="element-235" data-mp="3nksfun6822du">
            Evidence
          </p>
          <h2 id="evidence-heading" className="mt-1 text-lg font-semibold text-white sm:text-xl" data-id="element-236" data-mp="1y6vqv9h2rbv0">
            Locked evidence
          </h2>
        </div>
        <span className="inline-flex items-center gap-1.5 text-xs text-forensic-muted" data-id="element-237" data-mp="1t941am3wk1tz">
          <Lock size={12} className="text-forensic-crimson-soft" aria-hidden="true" data-id="element-238" data-mp="2iwzqtw0zxb22" />
          Redacted
        </span>
      </div>

      <div className="overflow-hidden rounded-2xl border border-forensic-line bg-forensic-surface" data-id="element-239" data-mp="2rvuxtabv6p6f">
        <ul className="divide-y divide-forensic-line" data-id="element-240" data-mp="2wv7gdivpda37">
          {rows.map((row, idx) => <li key={`${row.name}-${idx}`} className="px-5 py-5 sm:px-6" data-id="element-241" data-mp="31u0tha72uz2u" data-mpi={idx}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6" data-id="element-242" data-mp="31ry7d191pq3e">
                <p className="text-sm font-medium text-white sm:w-48 sm:shrink-0" data-id="element-243" data-mp="35t9p6kloh711">{row.name}</p>
                <div className="flex-1 space-y-2 blur-[1.5px]" aria-hidden="true" data-id="element-244" data-mp="3amumeafg5wz6">
                  <div className="h-2 rounded-full bg-white/[0.09]" style={{
                width: REDACTION_WIDTHS[idx % REDACTION_WIDTHS.length]
              }} data-id="element-245" data-mp="3fknw3dfyl947" />
                  <div className="h-2 rounded-full bg-white/[0.05]" style={{
                width: REDACTION_WIDTHS[(idx + 2) % REDACTION_WIDTHS.length]
              }} data-id="element-246" data-mp="3pjv73tz80ro5" />
                </div>
                <p className="inline-flex items-center gap-1.5 text-xs text-forensic-muted sm:w-40 sm:shrink-0 sm:justify-end" data-id="element-247" data-mp="18yzxbqdxuet7">
                  {row.locked ? <>
                      <Lock size={12} className="text-forensic-crimson-soft" aria-hidden="true" data-id="element-248" data-mp="z0k3vgrfutgj" />
                      Locked evidence
                    </> : row.meta}
                </p>
              </div>
            </li>)}
        </ul>
        <div className="flex flex-col gap-4 border-t border-forensic-line bg-forensic-card/60 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6" data-id="element-249" data-mp="1mulj7oovkihe">
          <p className="text-sm text-forensic-muted" data-id="element-250" data-mp="2fe0tnx6vkz0b">
            Exact evidence is available in the full forensic report.
          </p>
          <UnlockButton label={`Unlock Evidence — ${formatUsd(PLAN_PRICES_USD.single_scan)}`} state={state} onClick={onUnlock} variant="outline" data-id="element-251" data-mp="2ak64ie8i3rtu" />
        </div>
      </div>
    </section>;
}